import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import { adminNotificationApi } from "../utility/adminNotificationApi";
import { getAdminEnvironment, getEnvironmentTokenKey } from "./adminEnvironment";

const getPushTokenStorageKey = () =>
  `${getEnvironmentTokenKey(getAdminEnvironment(), "access")}:web-push`;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
let setupPromise: Promise<AdminWebPushSetupResult> | null = null;

export type AdminWebPushSetupResult = {
  status: "enabled" | "denied" | "unsupported" | "unconfigured";
  unsubscribeForeground?: () => void;
};

const isConfigured = () => Object.values(firebaseConfig).every(Boolean) && Boolean(vapidKey);

const getFirebaseApp = (): FirebaseApp =>
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

const handleForegroundMessage = (payload: MessagePayload) => {
  const title = payload.notification?.title || payload.data?.title || "새 관리자 알림";
  const body = payload.notification?.body || payload.data?.body || "새 알림이 도착했습니다.";
  const url = payload.data?.url || "/dashboard";

  if (Notification.permission === "granted") {
    const notification = new Notification(title, {
      body,
      icon: "/assets/quazar_icon.png",
      data: payload.data || {},
    });
    notification.onclick = () => {
      window.focus();
      window.location.assign(url);
      notification.close();
    };
  }

  window.dispatchEvent(new CustomEvent("quazar-admin-push", { detail: payload }));
  window.dispatchEvent(new Event("quazar-admin-notification-refresh"));
};

const setup = async (): Promise<AdminWebPushSetupResult> => {
  if (!isConfigured()) return { status: "unconfigured" };
  if (!(await isSupported())) return { status: "unsupported" };

  const permission = Notification.permission === "default"
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== "granted") return { status: "denied" };

  const messaging = getMessaging(getFirebaseApp());
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js",
    { scope: "/" },
  );
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration });
  if (!token) return { status: "unsupported" };

  await adminNotificationApi.registerWebPushToken({
    token,
    browserInfo: navigator.userAgent,
  });
  localStorage.setItem(getPushTokenStorageKey(), token);

  return { status: "enabled", unsubscribeForeground: onMessage(messaging, handleForegroundMessage) };
};

export const setupAdminWebPush = () => {
  if (!setupPromise) {
    setupPromise = setup().catch((error) => {
      console.warn("관리자 웹 알림을 초기화하지 못했습니다.", error);
      return { status: "unsupported" as const };
    });
  }
  return setupPromise;
};

export const disableAdminWebPush = async () => {
  const token = localStorage.getItem(getPushTokenStorageKey());
  if (!token) return;

  try {
    await adminNotificationApi.unregisterWebPushToken({ token });
    if (isConfigured() && await isSupported() && getApps().length > 0) {
      await deleteToken(getMessaging(getApp()));
    }
  } catch (error) {
    console.warn("관리자 웹 알림 토큰을 해제하지 못했습니다.", error);
  } finally {
    localStorage.removeItem(getPushTokenStorageKey());
    setupPromise = null;
  }
};
